// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Workflows (Settings → Workflows)
//  ----------------------------------------------------------------------------
//  The page where a person writes the words their life is filed under. Two
//  screens: the workflows an account has, and one workflow's statuses.
//
//  ── why this is its own file, in a surface that admits it is temporary ─────
//  md-settings.jsx's own header calls itself "an INVENTED SURFACE… NOT designed,
//  and expected to be replaced". This page is the first thing in there that
//  deserves better than the room holding it, so it borrows the room's parts —
//  SRow, SSection, write-through with no Save button — and keeps its own
//  grammar in a file that can move out whole when the room is rebuilt.
//
//  ── the two decisions the whole page is shaped by ─────────────────────────
//  SIX SECTIONS, ALWAYS ALL SIX, in spine order. A category nothing stands in
//  shows its header and its `+` and nothing else, because this app already
//  holds that "there is a difference between not drawing a place and not
//  having one" (WsEmptyRail, md-projects.jsx). A page that hid Paused until you
//  owned a Paused status would be a page that never told you Paused existed.
//
//  THE `+` YOU PRESS IS THE CATEGORY. It opens a draft row INSIDE that section
//  — mark, name, Cancel / Create — and creates a status of that category
//  without asking anything else. The design this replaced put a modal with a
//  six-item dropdown of internal enum values in front of somebody halfway
//  through a thought. The heading is the answer that dropdown was going to ask
//  for, which is the whole reason the spine is drawn as six headed sections.
//
//  Everything writes through a `Try*` door and NOTHING is drawn optimistically:
//  the list is rendered from storage every time, so a refused change leaves the
//  page showing what is actually saved, with the refusal said in a sentence
//  underneath. A page that painted the change and swallowed `last_backlog`
//  would be a page where the `+` sometimes silently does nothing.
//
//  Exposes <WorkflowsPage>.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStW, useEffect: useEfW, useRef: useRfW } = React;
const { ink: inkW, surf: surfW, mdRgba: rgbaW, mdIsLight: isLightW, MDI: MDIW } = window;
const { useDbEscapeLayer: useEscapeW } = window;
/* The Workshop's parts, through the ONE door md-projects.jsx opens for them.
   A second DnD dialect written next door would be a second set of thresholds,
   a second hold-to-lift and a second Escape — this app has one hand, and this
   page borrows it rather than growing another. Read at RENDER time, not at
   module load: this file is evaluated before md-projects.jsx has published. */
const MdWsP = new Proxy({}, { get: (_t, name) => (window.MdWs || {})[name] });

/* The sentence, never the code. The six `last_*` are this feature's own; every
   other reason is one md-project.ts already answers with, so a screen holding
   both does not need two ways of reading a refusal.

   THERE ARE SIX BECAUSE THE FLOOR IS SIX (WK-2). Each says what that category
   is FOR rather than reciting the rule — "somewhere to set things down" is a
   sentence about a life, and "a workflow must retain at least one status of
   category PAUSED" is a sentence about a schema. The person reading it is
   trying to tidy their statuses, not to learn the model. */
const WF_REFUSAL = {
  agent: 'Where things stand is yours to say, not the Organizer’s.',
  builtin: 'That one ships with ManDana.',
  missing: 'That is no longer here.',
  invalid: 'That cannot be saved as it stands.',
  last_backlog: 'Every workflow needs somewhere to start.',
  last_planned: 'Every workflow needs somewhere to plan.',
  last_progress: 'Every workflow needs somewhere to be in the middle of.',
  last_paused: 'Every workflow needs somewhere to set things down.',
  last_completed: 'Every workflow needs somewhere to finish.',
  last_cancelled: 'Every workflow needs somewhere to let go.',
  storage_full: 'There was no room to save that — this device is out of storage.',
};

const wfSay = (result) => (result && result.ok ? null
  : WF_REFUSAL[result && result.reason] || 'This change could not be saved.');

const wfPlural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/* ── one draft line ───────────────────────────────────────────────────────
   The same shape serves a new status and a rename, because they are the same
   gesture: a word being written in place. It carries its own Escape LAYER
   rather than a keydown listener, so leaving the draft leaves the draft and
   not the sheet behind it — the P0-3 regression, in a brand new surface. */
function WfDraft({ category, value, placeholder, accent, label, mark, onSave, onCancel }) {
  const [text, setText] = useStW(value || '');
  const ref = useRfW(null);
  useEscapeW(onCancel);
  useEfW(() => { if (ref.current) ref.current.focus(); }, []);
  return <div data-md-wf-draft={category || 'rename'}
    style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px' }}>
    <MdWsP.StatusMark category={category} color={null} size={13} />
    <input ref={ref} value={text} placeholder={placeholder} dir="auto"
      aria-label={placeholder} {...(mark ? { [mark]: '' } : {})}
      onChange={(event) => setText(event.target.value)}
      onKeyDown={(event) => { if (event.key === 'Enter') onSave(text); }}
      style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
        color: inkW(0.92), font: 'inherit', fontSize: 14.5 }} />
    <button type="button" aria-label="Cancel" onClick={onCancel}
      style={{ background: 'transparent', border: 'none', cursor: 'pointer',
        color: inkW(0.45), fontSize: 13 }}>Cancel</button>
    <button type="button" aria-label={label} onClick={() => onSave(text)}
      style={{ background: rgbaW(accent, 1), border: 'none', borderRadius: 8, cursor: 'pointer',
        color: '#fff', fontSize: 13, fontWeight: 650, padding: '5px 10px' }}>
      {label.split(' ')[0]}
    </button>
  </div>;
}

/* ── the ⋯ menu on a row ──────────────────────────────────────────────────
   Rename · Colour · Move to category ▸ · Delete. It is a DbMenu, so it shares
   the ONE escape stack instead of listening for the key itself. */
function WfRowMenu({ status, accent, onClose, onRename, onColor, onMove, onDelete }) {
  const DbMenuW = window.DbMenu;
  return <DbMenuW title={status.name} onClose={onClose}>
    <button type="button" className="md-db-menu-row" aria-label="Rename"
      onClick={onRename}><span>Rename</span></button>
    {/* Five hexes in a row was a palette, not a picker — one version of each
        hue, so "a slightly deeper green" was a thing somebody could want and
        the app could not hear. md-color.jsx owns the grid, and owns it for the
        domain colour control too rather than being written twice (WR-6). */}
    {window.MdColorPicker
      ? <window.MdColorPicker value={status.color || null} onPick={onColor}
          label={`Colour — ${status.name}`} />
      : null}
    {(window.MD_SPINE || []).filter((row) => row.id !== status.category).map((row) =>
      <button key={row.id} type="button" className="md-db-menu-row"
        aria-label={`Move to ${row.label}`} onClick={() => onMove(row.id)}>
        <MdWsP.StatusMark category={row.id} color={null} size={12} />
        <span>Move to {row.label}</span>
      </button>)}
    <button type="button" className="md-db-menu-row" aria-label="Delete"
      onClick={onDelete}><span style={{ color: '#e06c75' }}>Delete</span></button>
  </DbMenuW>;
}

/* ── the ⋯ button, and where its menu is allowed to be ────────────────────
   Every section is a Glass with `overflow:hidden` — that clip is what gives the
   rows one rounded card to sit inside. A menu rendered in there is open and cut
   off at the same time: the first row showed three items of four, the last row
   showed almost none. The database surface hit this exact wall in its views
   strip and answered it with `DbAnchoredMenu` (md-database.jsx) — a portal to
   the body, anchored under the button it came from, flipping above itself when
   the room is below. Borrow that answer; a second one would be a second set of
   rules about where menus live.

   The ref hangs on the wrapper rather than the button because a component is
   the only place a per-row ref can be held — hooks cannot be called from inside
   the map that draws the rows. */
function WfRowActions({ status, accent, open, onToggle, ...rest }) {
  const anchor = useRfW(null);
  const AnchoredW = window.DbAnchoredMenu;
  return <span ref={anchor} style={{ position: 'relative', display: 'inline-flex' }}>
    <button type="button" aria-label={`Status actions — ${status.name}`} onClick={onToggle}
      style={{ background: 'transparent', border: 'none', cursor: 'pointer',
        color: inkW(0.5), padding: 2, fontSize: 15 }}>•••</button>
    {open && <AnchoredW anchor={anchor}>
      <WfRowMenu status={status} accent={accent} {...rest} />
    </AnchoredW>}
  </span>;
}

/* ── the box, in three states ─────────────────────────────────────────────
   Empty, half-filled, filled — the same three the Projects window's lens box
   draws, because a person who has learned one tri-state box in this app should
   not have to learn a second one. The MEANINGS are this page's own. */
function WfBox({ state, color }) {
  const on = state === 'here';
  const half = state === 'inherited';
  return <span aria-hidden="true" style={{ flex: 'none', width: 14, height: 14, borderRadius: 4,
    border: `1.5px solid ${on || half ? color : inkW(0.28)}`,
    background: on ? color : 'transparent',
    boxShadow: half ? `inset 0 0 0 3px ${color}` : 'none' }} />;
}

/* ── "Used by" ────────────────────────────────────────────────────────────
   The Projects window's switcher is a LENS: transient, never persisted,
   answering "what am I looking at". This one is persistent and answers "what
   does this CHANGE". One shape meaning two opposite things is the thing this
   must not be, so it takes the grammar — the domain-grouped tree, the tri-state
   box, aria-checked="mixed" — and is called **Used by**, never a lens.

   THREE STATES, BECAUSE TWO WOULD LIE. A two-state box would draw a project
   covered by its pursuit exactly like one assigned individually, and then a
   change to the pursuit would appear to do nothing to one of them.

   • off       resolves elsewhere — the row NAMES what, greyed, so the tree can
               never lie about what will happen
   • inherited covered by a parent that is set here; nothing stored of its own
   • here      this node carries the assignment

   CHECKING A PURSUIT WRITES ONE ASSIGNMENT, never one per project — that IS the
   cascade, and it is what lets a later change flow to everything underneath.
   Unchecking a row that a parent is covering writes ONE project-level exception
   pointing at the account's default: the breakout, visible as a row that
   disagrees with its parent rather than as a silent divergence. */
function WfUsedBy({ flow, accent, onClose, onChange }) {
  const DbMenuW = window.DbMenu;
  const [query, setQuery] = useStW('');
  const pursuits = window.mdPursuitIndex ? window.mdPursuitIndex() : [];
  const projects = window.mdProjectIndex ? window.mdProjectIndex() : [];
  const flows = window.mdWorkflowIndex ? window.mdWorkflowIndex() : [];
  const nameOf = (id) => (flows.find((row) => row.id === id) || {}).name || '';

  const stateOf = (kind, id) => {
    if (window.mdWorkflowUse(kind, id) === flow.id) return 'here';
    return window.mdWorkflowFor(kind, id).id === flow.id ? 'inherited' : 'off';
  };
  /* ONE CLICK ALWAYS TOGGLES THE ROW'S OWN ASSIGNMENT, and there are exactly
     three ways that lands:

     • it says something of its own → take that back, and the row falls to
       whatever covers it. (This is how a breakout is undone: the same click.)
     • it says nothing and a parent is covering it with THIS workflow → the only
       way to disagree is to name another, so the breakout points at the
       account's default. Writing "not this" is not a thing storage can hold.
     • it says nothing and resolves elsewhere → say this workflow, here. */
  const press = (kind, id) => {
    const own = window.mdWorkflowUse(kind, id);
    const next = own ? null
      : stateOf(kind, id) === 'inherited' ? window.MD_WORKFLOW_DEFAULT_ID : flow.id;
    if (next === flow.id && !own && stateOf(kind, id) === 'inherited') return;
    window.mdWorkflowTrySetUse(kind, id, next);
    onChange();
  };

  const needle = query.trim().toLocaleLowerCase();
  const fits = (name) => !needle || name.toLocaleLowerCase().includes(needle);

  const groups = (window.MD_DOMAINS || []).map((domain) => {
    const held = pursuits.filter((row) => row.domainId === domain.id);
    const loose = projects.filter((row) => row.domainId === domain.id && !row.pursuitId);
    return { domain, pursuits: held, loose };
  }).filter((group) => group.pursuits.length || group.loose.length);

  /* A row's OWN state is what the data attribute says and what a click acts on.
     What it DRAWS also answers for everything under it: a domain nothing has
     been said about, holding a pursuit that has, is not "off" to a reader — it
     is partly in, and the half-filled box says so. Keeping the two apart is why
     checking a domain still writes exactly one assignment. */
  const row = (kind, node, label, depth, partly = false) => {
    const state = stateOf(kind, node.id);
    const shown = state === 'off' && partly ? 'inherited' : state;
    return <button key={`${kind}:${node.id}`} type="button" data-md-wf-use={`${kind}:${node.id}`}
      data-md-wf-state={state} role="menuitemcheckbox" className="md-db-menu-row"
      aria-checked={shown === 'here' ? 'true' : shown === 'inherited' ? 'mixed' : 'false'}
      onClick={() => press(kind, node.id)}
      style={{ minHeight: 32, paddingInlineStart: 8 + depth * 14 }}>
      <WfBox state={shown} color={rgbaW(accent, 1)} />
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' }}>{label}</span>
      {state === 'off' && <span style={{ color: inkW(0.33), fontSize: 10.5 }}>
        {nameOf(window.mdWorkflowFor(kind, node.id).id)}</span>}
    </button>;
  };

  return <DbMenuW title="Used by" onClose={onClose} style={{ position: 'absolute', top: 28,
    insetInlineEnd: 0, width: 260, maxHeight: 'min(380px, 60vh)', overflowY: 'auto', zIndex: 60 }}>
    <div data-md-wf-usedby role="menu" aria-label="Used by">
      {pursuits.length + projects.length > 7 && <input aria-label="Find" placeholder="Find…"
        value={query} onChange={(event) => setQuery(event.target.value)}
        style={{ width: 'calc(100% - 10px)', height: 32, margin: '2px 5px 6px', border: 'none',
          borderRadius: 'var(--md-radius-control, 8px)', outline: 'none', padding: '0 9px',
          color: inkW(0.9), background: inkW(0.07), font: 'inherit', fontSize: 12.5 }} />}
      {groups.map((group) => <div key={group.domain.id} role="group"
        aria-label={group.domain.label}>
        {row('domain', group.domain, group.domain.label, 0,
          group.pursuits.some((one) => stateOf('pursuit', one.id) !== 'off')
          || projects.some((item) => item.domainId === group.domain.id
            && stateOf('project', item.id) !== 'off'))}
        {group.pursuits.filter((one) => fits(one.name)).map((one) => <React.Fragment key={one.id}>
          {row('pursuit', one, one.name, 1)}
          {projects.filter((item) => item.pursuitId === one.id && fits(item.name))
            .map((item) => row('project', item, item.name, 2))}
        </React.Fragment>)}
        {group.loose.filter((one) => fits(one.name)).map((one) => row('project', one, one.name, 1))}
      </div>)}
      {!groups.length && <div style={{ padding: '8px 10px 10px', color: inkW(0.4),
        fontSize: 12.5 }}>Nothing to give this to yet.</div>}
    </div>
  </DbMenuW>;
}

/* ── "move them to…" ──────────────────────────────────────────────────────
   The one sheet in this page, and it exists because deleting a status is the
   only gesture here that can quietly damage something somebody wrote. It names
   the number it is about to MOVE — which is not always the number the row
   shows, since everything that has never said stands in Inbox and has nothing
   to rewrite — and offers the OTHER STATUSES OF THE SAME CATEGORY FIRST,
   because a status being retired is nearly always being replaced by its
   neighbour. Inbox is the default, since it is the place guaranteed to exist.

   It is never opened onto a question whose answer is no: the floor is asked
   first, and a status nothing stands on is simply removed. */
function WfDeleteSheet({ flow, status, held, accent, onClose, onConfirm }) {
  const others = flow.statuses.filter((row) => row.id !== status.id);
  const near = others.filter((row) => row.category === status.category);
  const rest = others.filter((row) => row.category !== status.category);
  const options = [...near, ...rest];
  const inbox = window.mdWorkflowInboxOf(flow.id);
  const [pick, setPick] = useStW(inbox && inbox.id !== status.id
    ? inbox.id : (options[0] || {}).id || null);
  useEscapeW(onClose);
  const counts = [held.projects ? wfPlural(held.projects, 'project', 'projects') : null,
    held.records ? wfPlural(held.records, 'record', 'records') : null].filter(Boolean).join(' and ');
  return <div data-md-wf-delete role="dialog" aria-modal="true"
    aria-label={`Delete ${status.name}`}
    style={{ marginTop: 12, padding: '14px 16px', borderRadius: 16,
      border: `1px solid ${inkW(0.12)}`, background: surfW(0.10) }}>
    <p style={{ margin: '0 0 10px', color: inkW(0.8), fontSize: 13.5, lineHeight: 1.5 }}>
      {counts} {held.projects + held.records === 1 ? 'uses' : 'use'}{' '}
      <strong>{status.name}</strong>. Move {held.projects + held.records === 1 ? 'it' : 'them'} to…
    </p>
    <div role="listbox" aria-label="Move them to">
      {options.map((row) => <button key={row.id} type="button" role="option"
        aria-selected={pick === row.id ? 'true' : 'false'} onClick={() => setPick(row.id)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
          background: pick === row.id ? surfW(0.14) : 'transparent', border: 'none',
          borderRadius: 10, cursor: 'pointer', padding: '8px 10px', color: inkW(0.88),
          font: 'inherit', fontSize: 13.5 }}>
        <MdWsP.StatusMark category={row.category} color={row.color || null} size={12} />
        <span>{row.name}</span>
      </button>)}
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
      <button type="button" aria-label="Cancel" onClick={onClose}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer',
          color: inkW(0.5), fontSize: 13 }}>Cancel</button>
      <button type="button" aria-label="Move them and delete" onClick={() => onConfirm(pick)}
        style={{ background: '#e06c75', border: 'none', borderRadius: 8, cursor: 'pointer',
          color: '#fff', fontSize: 13, fontWeight: 650, padding: '6px 11px' }}>
        Move them and delete
      </button>
    </div>
  </div>;
}

/* ── one workflow's statuses ──────────────────────────────────────────────── */
function WfStatuses({ flowId, accent }) {
  const [, bump] = useStW(0);
  const [note, setNote] = useStW(null);
  const [draft, setDraft] = useStW(null);        // { category } | { statusId }
  const [menu, setMenu] = useStW(null);          // status id
  const [going, setGoing] = useStW(null);       // { id, held } — the delete sheet
  const [used, setUsed] = useStW(false);        // the "Used by" tree, open
  const redraw = () => bump((n) => n + 1);

  /* True if it landed. A refusal says why on the way past AND leaves the draft
     standing with the word still in it — retyping something the app refused to
     save is the app charging you for its own wall. */
  const commit = (result) => {
    const said = wfSay(result);
    setNote(said);
    if (!said) redraw();
    return !said;
  };

  const flow = window.mdWorkflowById(flowId);
  const statuses = flow ? flow.statuses : [];
  const usage = flow && window.mdProjectStatusUsage
    ? window.mdProjectStatusUsage(flow.id) : {};

  /* ── the hand (WF-9) ────────────────────────────────────────────────────
     THE APP'S OWN, borrowed from md-projects.jsx rather than written again:
     the movement threshold that keeps a click a click, touch hold-to-lift, the
     shared Escape layer, and a ghost that is moved rather than re-rendered.

     Inside a section the drop REORDERS; across a boundary it RE-CATEGORISES,
     and the mark changes as the row lands — which is the whole explanation of
     what just happened. One gesture, one door, one write.

     THE POSITION IS A MIDPOINT, not a renumbering. Positions only ever matter
     within a category (the read sorts by spine rank first), so a row landing
     between two neighbours takes the number between theirs and every other row
     is left alone. One status changes, so ⌘Z restores one status. */
  const drop = (id, over) => {
    const category = over && over.zone;
    const moving = statuses.find((row) => row.id === id);
    if (!flow || !category || !moving) return false;
    const seat = statuses.filter((row) => row.category === category && row.id !== id);
    let at = seat.length;                                     // the foot, by default
    if (over.id && over.id !== id) {
      const beside = seat.findIndex((row) => row.id === over.id);
      if (beside >= 0) at = beside + (over.edge === 'after' ? 1 : 0);
    } else if (moving.category === category) {
      /* Released over its own row, or over blank space in the section it was
         already in. Neither is somebody asking for the foot of the section —
         the hand nulls the target when it is the row being carried, so "no
         target" here means the gesture answered nothing. Sending the row to
         the foot on that would make a mis-click reorder the page. The foot is
         still reachable the honest way: over the lower half of the last row. */
      return false;
    }
    const above = at > 0 ? seat[at - 1].position : null;
    const below = at < seat.length ? seat[at].position : null;
    const position = above == null && below == null ? 1
      : above == null ? below - 1
        : below == null ? above + 1
          : (above + below) / 2;
    // A drop that answers the question it was already answering is not a change.
    if (moving.category === category && moving.position === position) return false;
    return commit(window.mdWorkflowTryStatusMove(flow.id, id, category, position));
  };
  const hand = MdWsP.useWsHand({ canDrag: true, onDrop: drop, touchHold: true });

  if (!flow) return null;

  /* Deleting, in three honest endings: the floor says no and the sentence says
     so; nothing is standing on it and it simply goes; something is, and the
     sheet asks where that something lands. */
  const ask = (status) => {
    const wall = window.mdWorkflowStatusRemovable(flow.id, status.id);
    if (wall) { setNote(WF_REFUSAL[wall] || 'This change could not be saved.'); return; }
    const held = window.mdProjectStatusHolders
      ? window.mdProjectStatusHolders(flow.id, status.id) : { projects: 0, records: 0 };
    if (!held.projects && !held.records) {
      commit(window.mdWorkflowTryStatusDelete(flow.id, status.id));
      return;
    }
    setNote(null);
    setGoing({ id: status.id, held });
  };

  const stand = (statusId) => {
    const held = usage[statusId];
    if (!held || (!held.projects && !held.records)) return null;
    return [held.projects ? wfPlural(held.projects, 'project', 'projects') : null,
      held.records ? wfPlural(held.records, 'record', 'records') : null]
      .filter(Boolean).join(' · ');
  };

  /* `position: relative` because the hand's ghost is absolute and measures
     from its nearest positioned ancestor — see useWsHand's own note on why it
     is not fixed. */
  return <div style={{ position: 'relative' }}>
    {/* The flow's name is the SHEET's title now (Polish 5), and the way back
        is the sheet's × — so the only thing this row holds is Used by. */}
    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 0 10px' }}>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <button type="button" aria-label="Used by" aria-haspopup="menu"
          aria-expanded={used ? 'true' : 'false'} onClick={() => setUsed(!used)}
          style={{ background: 'transparent', border: `1px solid ${inkW(0.12)}`,
            borderRadius: 'var(--md-radius-control, 8px)', cursor: 'pointer', padding: '5px 9px',
            color: inkW(0.7), font: 'inherit', fontSize: 12.5 }}>Used by</button>
        {used && <WfUsedBy flow={flow} accent={accent} onClose={() => setUsed(false)}
          onChange={redraw} />}
      </span>
    </div>
    <p style={{ color: inkW(0.45), fontSize: 12.5, lineHeight: 1.5, margin: '0 4px 12px' }}>
      Six places anything can stand, and the words you use for them. The six are
      ManDana’s; the words are yours.
    </p>
    {note && <p data-md-wf-note style={{ color: '#e0a06c', fontSize: 12.5, lineHeight: 1.5,
      margin: '0 4px 12px' }}>{note}</p>}

    {(window.MD_SPINE || []).map((category) => {
      const held = flow.statuses.filter((row) => row.category === category.id);
      return <div key={category.id} style={{ marginTop: 18 }}>
        <div data-md-wf-section={category.id}
          style={{ display: 'flex', alignItems: 'center', padding: '0 4px 8px' }}>
          <span className="md-label" style={{ flex: 1, color: inkW(0.4), fontSize: 11,
            fontWeight: 400 }}>{category.label}</span>
          <button type="button" aria-label={`Add a ${category.label} status`}
            onClick={() => { setNote(null); setDraft({ category: category.id }); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4,
              display: 'flex', color: rgbaW(accent, 0.95) }}>
            {MDIW.plus(rgbaW(accent, 0.95), 16)}
          </button>
        </div>
        <window.Glass radius={18} alpha={0.08} style={{ overflow: 'hidden' }}>
          <div data-md-wf-body={category.id} data-md-drop-zone={category.id}>
            {held.map((status) => (draft && draft.statusId === status.id)
              ? <WfDraft key={status.id} category={status.category} value={status.name}
                placeholder="Status name" accent={accent} label="Save name"
                onCancel={() => setDraft(null)}
                onSave={(text) => {
                  if (!text.trim()) return;
                  if (commit(window.mdWorkflowTryStatusRename(flow.id, status.id, text))) setDraft(null);
                }} />
              : <div key={status.id} data-md-wf-status={status.id} data-md-drag-id={status.id}
                onPointerDown={(event) => hand.start(event, status.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                  cursor: 'grab', touchAction: 'none',
                  opacity: hand.drag && hand.drag.id === status.id ? 0.35 : 1,
                  boxShadow: MdWsP.wsLanding(hand.drag, status.id)
                    ? MdWsP.wsLandingLine(MdWsP.wsLanding(hand.drag, status.id), rgbaW(accent, 1))
                    : 'none',
                  borderBottom: `1px solid ${inkW(0.06)}` }}>
                {/* THE ROW'S OWN COLOUR, or today's ink (WR-5). The accent is
                    the domain's, and lending it here made a status with no
                    colour look like one that had been given one. */}
                <MdWsP.StatusMark category={status.category}
                  color={status.color || null} size={13} />
                <span style={{ flex: 1, minWidth: 0, color: inkW(0.9), fontSize: 14.5 }}
                  dir="auto">{status.name}</span>
                {stand(status.id) && <span style={{ color: inkW(0.4), fontSize: 12 }}>
                  {stand(status.id)}</span>}
                <WfRowActions status={status} accent={accent} open={menu === status.id}
                  onToggle={() => { setNote(null); setMenu(menu === status.id ? null : status.id); }}
                  onClose={() => setMenu(null)}
                  onRename={() => { setMenu(null); setDraft({ statusId: status.id }); }}
                  /* The menu STAYS OPEN. Comparing two shades of the same hue
                     is the whole reason there is a grid rather than five
                     hexes, and a picker that closes on the first press makes a
                     person reopen it to see what they just chose. */
                  onColor={(hue) =>
                    commit(window.mdWorkflowTryStatusSetColor(flow.id, status.id, hue))}
                  onMove={(to) => { setMenu(null);
                    commit(window.mdWorkflowTryStatusMove(flow.id, status.id, to)); }}
                  onDelete={() => { setMenu(null); ask(status); }} />
              </div>)}
            {draft && draft.category === category.id
              && <WfDraft category={category.id} placeholder="Status name" accent={accent}
                label="Create status" onCancel={() => setDraft(null)}
                onSave={(text) => {
                  if (!text.trim()) return;
                  if (commit(window.mdWorkflowTryStatusCreate(flow.id, category.id, text))) setDraft(null);
                }} />}
          </div>
        </window.Glass>
      </div>;
    })}
    {going && statuses.find((row) => row.id === going.id)
      && <WfDeleteSheet flow={flow} status={statuses.find((row) => row.id === going.id)}
        held={going.held} accent={accent} onClose={() => setGoing(null)}
        onConfirm={(to) => {
          if (commit(window.mdWorkflowTryStatusDelete(flow.id, going.id, to))) setGoing(null);
        }} />}
    {hand.carried}
  </div>;
}

/* ── the workflows an account has ─────────────────────────────────────────── */
/* CONTROLLED since Polish 5: the settings sheet owns which screen stands —
   `flowId` names the workflow whose statuses to show, and a pressed row asks
   through `onOpen` instead of swapping the page itself. That is what lets the
   sheet title every screen and its × step back exactly one level. A flowId
   that no longer names a flow falls back to the list. */
function WorkflowsPage({ accent, flowId, onOpen }) {
  const [, bump] = useStW(0);
  const [note, setNote] = useStW(null);
  const [naming, setNaming] = useStW(false);

  if (flowId && window.mdWorkflowById(flowId)) {
    return <WfStatuses flowId={flowId} accent={accent} />;
  }

  const flows = window.mdWorkflowIndex ? window.mdWorkflowIndex() : [];
  return <>
    <p style={{ color: inkW(0.45), fontSize: 12.5, lineHeight: 1.5, margin: '0 4px 12px' }}>
      A workflow is a set of words for where things stand. Give one to a part of
      life and everything under it speaks the same words, unless it says otherwise.
    </p>
    {note && <p data-md-wf-note style={{ color: '#e0a06c', fontSize: 12.5, lineHeight: 1.5,
      margin: '0 4px 12px' }}>{note}</p>}
    <window.Glass radius={18} alpha={0.08} style={{ overflow: 'hidden' }}>
      {flows.map((flow) => <button key={flow.id} type="button" aria-label={flow.name}
        onClick={() => onOpen && onOpen(flow.id)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start',
          background: 'transparent', border: 'none', cursor: 'pointer', padding: '13px 16px',
          borderBottom: `1px solid ${inkW(0.06)}` }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', color: inkW(0.92), fontSize: 15, fontWeight: 550 }}
            dir="auto">{flow.name}</span>
          <span style={{ display: 'block', color: inkW(0.45), fontSize: 12.5, marginTop: 2 }}>
            {wfPlural(flow.statuses.length, 'status', 'statuses')}</span>
        </span>
        {MDIW.chevron(inkW(0.3), 9)}
      </button>)}
      {naming
        ? <WfDraft category={null} placeholder="Workflow name" accent={accent}
          label="Create workflow" mark="data-md-wf-name" onCancel={() => setNaming(false)}
          onSave={(text) => {
            if (!text.trim()) return;
            const made = window.mdWorkflowTryCreate(text);
            const said = wfSay(made);
            setNote(said);
            if (!said) { setNaming(false); bump((n) => n + 1); if (onOpen) onOpen(made.id); }
          }} />
        : <button type="button" aria-label="New workflow" onClick={() => { setNote(null); setNaming(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
            background: 'transparent', border: 'none', cursor: 'pointer', padding: '13px 16px',
            color: rgbaW(accent, 0.95), fontSize: 14.5 }}>
          {MDIW.plus(rgbaW(accent, 0.95), 15)}<span>New workflow</span>
        </button>}
    </window.Glass>
  </>;
}

Object.assign(window, { WorkflowsPage });
